import { useState, useEffect } from "react";
import { useI18n } from "../lib/i18n.jsx";
import { db } from "../lib/firebase.js";
import { doc, getDoc } from "firebase/firestore";
import { publishMarketplaceTeam, getTeamVersions } from "../lib/api.js";
import { navigateTo } from "../App.jsx";

export default function EditTeam({ teamId, user, onBack }) {
  const { t } = useI18n();
  const [form, setForm] = useState(null);
  const [versions, setVersions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const snap = await getDoc(doc(db, "marketplace_teams", teamId));
      if (!snap.exists()) { navigateTo("/my-teams"); return; }
      const data = snap.data();
      if (data.ownerId !== user?.uid) { navigateTo("/my-teams"); return; }
      setForm({
        name: data.name || "",
        slug: data.slug || "",
        description_en: data.description_en || "",
        description_es: data.description_es || "",
        description_pt: data.description_pt || "",
        short_desc_en: data.short_desc_en || "",
        short_desc_es: data.short_desc_es || "",
        short_desc_pt: data.short_desc_pt || "",
        category: data.category || "",
        tags: (data.tags || []).join(", "),
        price: String(data.price || "0"),
        repoUrl: data.repoUrl || "",
        tag: data.defaultTag || "",
        screenshots: (data.screenshots || []).join("\n"),
        ownerName: user?.displayName || user?.email || "",
      });
      const v = await getTeamVersions(teamId);
      setVersions(v);
      if (v.length > 0 && !data.defaultTag) setForm((f) => ({ ...f, tag: v[0].id }));
      setLoading(false);
    }
    load();
  }, [teamId, user]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await publishMarketplaceTeam({
        teamId,
        ...form,
        price: parseFloat(form.price) || 0,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        screenshots: form.screenshots.split("\n").map((s) => s.trim()).filter(Boolean),
        update: true,
      });
      onBack();
    } catch (e) {
      setError(e.message);
    }
    setSubmitting(false);
  }

  if (loading || !form) return <div className="loading-state">{t("loading")}</div>;

  return (
    <div className="publish-team">
      <button className="btn btn-ghost back-btn" onClick={onBack}>← {t("back")}</button>
      <h2>{t("edit_team")}: {form.name}</h2>

      <form className="publish-form" onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-field">
            <label>{t("team_name")}</label>
            <input className="form-input" value={form.name} onChange={(e) => set("name", e.target.value)} required />
          </div>
          <div className="form-field">
            <label>{t("team_slug")}</label>
            <input className="form-input" value={form.slug} onChange={(e) => set("slug", e.target.value)} required />
          </div>
        </div>

        <div className="form-field">
          <label>{t("team_category")}</label>
          <input className="form-input" value={form.category} onChange={(e) => set("category", e.target.value)} />
        </div>

        <div className="form-field">
          <label>{t("team_tags")}</label>
          <input className="form-input" value={form.tags} onChange={(e) => set("tags", e.target.value)} />
        </div>

        <div className="form-field">
          <label>{t("team_price")}</label>
          <input className="form-input" type="number" min="0" step="0.01" value={form.price}
                 onChange={(e) => set("price", e.target.value)} />
        </div>

        {versions.length > 0 && (
          <div className="form-field">
            <label>{t("latest_version")}</label>
            <select className="form-select" value={form.tag} onChange={(e) => set("tag", e.target.value)}>
              {versions.map((v) => <option key={v.id} value={v.id}>{v.id}</option>)}
            </select>
          </div>
        )}

        <div className="form-section">
          <h3>English</h3>
          <div className="form-field">
            <label>{t("team_short_desc")}</label>
            <input className="form-input" value={form.short_desc_en} onChange={(e) => set("short_desc_en", e.target.value)} />
          </div>
          <div className="form-field">
            <label>{t("team_description")}</label>
            <textarea className="form-textarea" rows={4} value={form.description_en}
                      onChange={(e) => set("description_en", e.target.value)} />
          </div>
        </div>

        <div className="form-section">
          <h3>Español</h3>
          <div className="form-field">
            <label>{t("team_short_desc")}</label>
            <input className="form-input" value={form.short_desc_es} onChange={(e) => set("short_desc_es", e.target.value)} />
          </div>
          <div className="form-field">
            <label>{t("team_description")}</label>
            <textarea className="form-textarea" rows={4} value={form.description_es}
                      onChange={(e) => set("description_es", e.target.value)} />
          </div>
        </div>

        <div className="form-section">
          <h3>Português</h3>
          <div className="form-field">
            <label>{t("team_short_desc")}</label>
            <input className="form-input" value={form.short_desc_pt} onChange={(e) => set("short_desc_pt", e.target.value)} />
          </div>
          <div className="form-field">
            <label>{t("team_description")}</label>
            <textarea className="form-textarea" rows={4} value={form.description_pt}
                      onChange={(e) => set("description_pt", e.target.value)} />
          </div>
        </div>

        <div className="form-field">
          <label>{t("team_screenshots")}</label>
          <textarea className="form-textarea" rows={3} value={form.screenshots}
                    onChange={(e) => set("screenshots", e.target.value)} />
        </div>

        {error && <div className="form-error">{error}</div>}

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? t("loading") : t("save")}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onBack}>{t("cancel")}</button>
        </div>
      </form>
    </div>
  );
}
